import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import dataTables from 'view/dataTables';
import { dataConfig } from 'data/dataConfig';
import { getReducerName, DATA_SOURCES, OPEN_DATA } from 'data/dataSourceConstants';

import DataSetsList, {
	buildDataSetItem,
} from 'pages/_rd_overview/dataSetsCard/DataSetsList';
import DataTableContent from 'pages/_rd_overview/dataSetsCard/DataTableContent';
import Logo from 'components/appHeader/Logo';

import { selectDataSet, selectCategory } from 'reducers/overviewDataSetReducer';

import './DataSetsCardVertical.css';


function countViolations(propertyData) {
	// counts[category] = sum of open issues in that category
	// e.g. counts = { violations: 143, infrastructure: 22, certifications: 4 }

	//to avoid double counting, these datasets shouldn't be added to totals, since they also exist as combined datasets
	let combinedDatasets = ['dobComplaints', 'dobViolations', 'ecbViolations', 'jobApplications', 'dobJobApplications'];

	let counts = {};
	Object.keys(dataTables).forEach((dataSet) => {
		let config = dataTables[dataSet];

		if (!config.isArchived && !config.sidebarOnly) {
			//data source sub-loop
			DATA_SOURCES.forEach((dataSource) => {
				if (config[dataSource]) {
					let category = config.dataCategory;
					let openIssueCountField = dataConfig[dataSet]?.openIssueCountFields[dataSource];
					let openIssueCount = parseInt(propertyData?.[openIssueCountField]) || 0;

					if (!combinedDatasets.includes(dataSet)) {
						if (counts[category]) {
							counts[category] += openIssueCount;
						} else {
							counts[category] = openIssueCount;
						}
					}
				}
			});
		}
	});
	return counts;
}

function DataSetsCardVertical() {
	const dispatch = useDispatch();
	let propertyData = useSelector((state) => state.property.propertyData || {});
	let overviewDataSet = useSelector((state) => state.overviewDataSet || {});
	let selectedCategory = overviewDataSet.selectedCategory || 'violation';
	let selectedDataSet = overviewDataSet.dataSet ? {
		dataSet: overviewDataSet.dataSet,
		dataSource: overviewDataSet.dataSource,
		reducerName: overviewDataSet.reducerName,
		title: overviewDataSet.title,
		columns: overviewDataSet.columns,
		tableView: overviewDataSet.tableView,
	} : null;

	let categoryViolationCounts = countViolations(propertyData);

	const categories = [
		{ key: 'violation', label: 'Violations' },
		{ key: 'infrastructure', label: 'Infrastructure' },
		{ key: 'certification', label: 'Certifications' },
	];

	const open = (item) => {
		const columns = item.isLegacyJobApplications
			? dataTables.jobApplicationsLegacy[item.dataSource].columns
			: item.config[item.dataSource].columns;
		let options = {
			dataSet: item.dataSet,
			reducerName: getReducerName(item.dataConfig, item.dataSource),
			dataSource: item.dataSource,
			title: item.config.title,
			columns,
			tableView: item.isLegacyJobApplications
				? 'legacy'
				: item.isPrimarySourced
					? 'grouped'
					: null,
		};

		dispatch(selectDataSet(options));
	};

	// Called by the DOB Complaints Resources dropdown inside DataTableContent
	const handleSwitchSource = (targetDataSet, targetDataSource) => {
		const item = buildDataSetItem(targetDataSet, targetDataSource, propertyData);
		if (item) open(item);
	};

	const [isInitialLoad, setIsInitialLoad] = useState(true);

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		//set initial selected dataset to 311 complaints open data
		if (isInitialLoad && propertyData?.id) {
			setIsInitialLoad(false); //don't reset the initial dataset every time propertyData comes through
			dispatch(selectCategory('violation'));
			open(buildDataSetItem('complaints311', OPEN_DATA, propertyData));
		}
	}, [propertyData]);

	function getDataSetList(contentName) {
		if (selectedCategory === 'violation') {
			return (
				<DataSetsList
					category={'violation'}
					onSelectDataSet={open}
					selectedDataSet={selectedDataSet}
				/>
			);
		} else if (selectedCategory === 'infrastructure') {
			return (
				<DataSetsList
					category={'infrastructure'}
					onSelectDataSet={open}
					selectedDataSet={selectedDataSet}
				/>
			);
		} else if (selectedCategory === 'certification') {
			return (
				<DataSetsList
					category={'certification'}
					onSelectDataSet={open}
					selectedDataSet={selectedDataSet}
				/>
			);
		}
	}

	const blankDataSet = (
		<div className="blank d-flex flex-column align-items-center justify-content-center w-100 h-100">
			<Logo />
			<p>Select a Dataset to view data</p>
		</div>
	);

	return (
		<div className="rd-overview-data-sets-card-vertical">
			<div className="building-info-horizontal-tabs">
				{categories.map((cat) => (
					<button
						key={cat.key}
						className={`building-info-tab-btn ${selectedCategory === cat.key ? 'active' : ''}`}
						onClick={() => dispatch(selectCategory(cat.key))}
						title={
							categoryViolationCounts[cat.key]
								? `${categoryViolationCounts[cat.key]} open`
								: undefined
						}
					>
						{cat.label}
					</button>
				))}
			</div>
			<div className="rd-overview-data-sets-card-vertical-content">
				<div className="rd-overview-data-sets-card-vertical-list-wrapper">
					{getDataSetList(selectedCategory)}
				</div>
				<div className="rd-overview-data-sets-card-vertical-table-wrapper">
					{selectedDataSet ? (
						<DataTableContent
							dataSet={selectedDataSet.dataSet}
							reducerName={selectedDataSet.reducerName}
							dataSource={selectedDataSet.dataSource}
							title={selectedDataSet.title}
							columns={selectedDataSet.columns}
							tableView={selectedDataSet.tableView}
							onSwitchSource={handleSwitchSource}
						/>
					) : (
						blankDataSet
					)}
				</div>
			</div>
		</div>
	);
}

export default DataSetsCardVertical;
